import { useMemo } from "react";
import type { BoardRow } from "@/lib/event-board";
import { QUICK_VIEWS, quickViewCount, type QuickViewKey } from "@/lib/event-filters";

interface EventsQuickViewsProps {
  rows: BoardRow[];
  today: Date;
  active: QuickViewKey | null;
  onApply: (key: QuickViewKey) => void;
  /** The contacts read failed, so "no reply" can't be worked out. */
  messagesUnavailable: boolean;
}

/** The three questions asked most, one click each; a second click resets (#1557). */
export function EventsQuickViews({ rows, today, active, onApply, messagesUnavailable }: EventsQuickViewsProps) {
  const counts = useMemo(
    () =>
      Object.fromEntries(QUICK_VIEWS.map((v) => [v.key, quickViewCount(rows, v, today)])) as Record<
        QuickViewKey,
        number
      >,
    [rows, today],
  );
  return (
    <div role="group" aria-label="Quick views" className="flex flex-wrap items-center gap-2">
      <span className="text-[11.5px] font-bold uppercase tracking-[.08em] text-muted-foreground">Quick views</span>
      {QUICK_VIEWS.map((v) => {
        const disabled = !!v.needsMessages && messagesUnavailable;
        return (
          <button
            key={v.key}
            type="button"
            aria-pressed={active === v.key}
            disabled={disabled}
            title={disabled ? "Couldn't load messages" : undefined}
            onClick={() => onApply(v.key)}
            className="inline-flex items-center gap-1.5 rounded-full border bg-card px-3 py-1 text-[13px] font-semibold hover:border-accent disabled:cursor-not-allowed disabled:opacity-50 aria-pressed:border-accent aria-pressed:bg-[hsl(var(--ev-accent-soft))]"
          >
            {v.label}
            <span className="text-xs font-bold text-muted-foreground">{disabled ? "–" : counts[v.key]}</span>
          </button>
        );
      })}
    </div>
  );
}
